import { Box, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import { useElementSize } from "hooks/useElementSize";
import {
  containsText,
  HighlightedText,
} from "src/components/common/HighlightedText";
import { countChipsThatFit } from "src/utils/chipFitting";
import { measureTextWidth } from "src/utils/measureTextWidth";

const CHIP_CHROME_WIDTH = 22;
const CHIP_GAP_WIDTH = 4;
const SOURCE_CODE_PRO_ADVANCE_EM = 0.6;

const chipWidth = (code: string, font: string): number =>
  (measureTextWidth({ text: code, font }) ??
    code.length * parseFloat(font) * SOURCE_CODE_PRO_ADVANCE_EM) +
  CHIP_CHROME_WIDTH;

export const codesThatFit = (
  codes: string[],
  availableWidth: number,
  font: string
): string[] =>
  codes.slice(
    0,
    countChipsThatFit({
      widths: codes.map((code) => chipWidth(code, font)),
      availableWidth,
      overflowWidth: chipWidth(`+${codes.length}`, font),
      gapWidth: CHIP_GAP_WIDTH,
    })
  );

interface Props {
  recipeCodes: string[];
  highlight?: string;
}

const MATCHED_CHIP_SX = { borderColor: "primary.main" } as const;

const matchFirst = (
  codes: string[],
  highlight: string | undefined
): string[] => {
  const firstMatch = codes.find((code) => containsText(code, highlight));
  return firstMatch === undefined
    ? codes
    : [firstMatch, ...codes.filter((code) => code !== firstMatch)];
};

export const RecipeCodesCell = ({ recipeCodes, highlight }: Props) => {
  const codes = matchFirst(recipeCodes, highlight);
  const theme = useTheme();
  const { ref, size } = useElementSize();
  const width = size?.width ?? null;

  const font = `${String(theme.typography.body2Mono.fontSize)} ${String(
    theme.typography.body2Mono.fontFamily
  )}`;

  const visibleCodes =
    width === null ? codes.slice(0, 1) : codesThatFit(codes, width, font);
  const hiddenCount = codes.length - visibleCodes.length;
  const hiddenCodeMatches = codes
    .slice(visibleCodes.length)
    .some((code) => containsText(code, highlight));

  if (codes.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        —
      </Typography>
    );
  }

  return (
    <Tooltip title={codes.join(", ")}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${CHIP_GAP_WIDTH}px`,
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {visibleCodes.map((code) => (
          <Chip
            key={code}
            label={
              <Typography variant="body2Mono" sx={{ whiteSpace: "nowrap" }}>
                <HighlightedText text={code} query={highlight} />
              </Typography>
            }
            variant="outlined"
            size="small"
            sx={containsText(code, highlight) ? MATCHED_CHIP_SX : undefined}
          />
        ))}
        {hiddenCount > 0 ? (
          <Chip
            label={
              <Typography variant="body2Mono">{`+${hiddenCount}`}</Typography>
            }
            variant="outlined"
            size="small"
            sx={hiddenCodeMatches ? MATCHED_CHIP_SX : undefined}
          />
        ) : null}
      </Box>
    </Tooltip>
  );
};
